export type WorkforceMode = 'resolving' | 'snapshot' | 'legacy' | 'blocked';
export type WorkforceFilter = 'department' | 'location' | 'division' | 'jobTitle';
export interface WorkforceEmployee {
  membership?: {memberId: string | null; reason: string | null};
  employeeKey: string;
  employeeNumber: string | null;
  firstName: string | null;
  lastName: string | null;
  primaryEmail: string | null;
  employmentStatus: string | null;
  activeEligibility: string | null;
  payrollParticipationStatus: string | null;
  jobTitle: string | null;
  department: string | null;
  location: string | null;
  division: string | null;
}
export interface WorkforceContext {
  mode: 'snapshot' | 'legacy';
  availability: 'ready' | 'legacy' | 'no_promoted_snapshot' | 'provider_disconnected' | 'snapshot_unavailable';
  scope: {companyId: string; providerSlug?: 'ukg_ready'; providerSlot?: 'primary'};
  snapshot: {id: string; generation: string; builtAt: string; sealedAt: string; promotedAt: string;
    populationCompleteness: string; sourceConsistency: string; warningCodes: string[];
    fieldCompleteness: Record<string, boolean | null>} | null;
  counts: {enumerated: string; browsable: string; observationOnly: string; filtered?: string} | null;
  synchronization?: {enabled: boolean; status: string; active: boolean; stage: string | null;
    lastAttemptOutcome: string | null; createdAt: string | null; startedAt: string | null;
    completedAt: string | null; updatedAt: string | null; safeFailureCode: string | null};
  capabilities: {employeeBrowsing: boolean; facets: false; rowDetails: boolean};
  filterOptions?: {mode: 'derived'; fields: WorkforceFilter[]; limit: number};
  serverTime: string;
}
export interface WorkforcePage extends WorkforceContext {
  employees: WorkforceEmployee[];
  nextCursor: string | null;
  previousCursor: string | null;
}
export interface WorkforceOptions extends WorkforceContext { values: string[]; nextCursor: string | null; }
export interface WorkforceApi {
  getWorkforceBrowseContext(companyId: string): Promise<WorkforceContext>;
  getWorkforceEmployees(companyId: string, query: Record<string, string>): Promise<WorkforcePage>;
  getWorkforceFilterOptions(companyId: string, query: Record<string, string>): Promise<WorkforceOptions>;
}

// One page, one options response, at most 50 navigation tokens and 100 minimal selection entries.
export class WorkforceBrowseState {
  readonly historyLimit = 50;
  readonly filterFields: WorkforceFilter[] = ['department', 'location', 'division', 'jobTitle'];
  readonly selection = new Map<string, {employeeKey: string; name: string}>();
  selectionRevision = 0;
  selectionSnapshotId: string | null = null;
  mode: WorkforceMode = 'resolving';
  companyId = '';
  context: WorkforceContext | null = null;
  page: WorkforcePage | null = null;
  options: string[] = [];
  optionsNext: string | null = null;
  loading = false;
  optionsLoading = false;
  error = '';
  notice = '';
  query: Record<string, string> = {sort: 'name', order: 'asc', searchField: 'lastName', search: ''};
  history: Array<string | null> = [];
  currentCursor: string | null = null;
  private version = 0;
  private optionsVersion = 0;
  private selectedOptionField: WorkforceFilter = 'department';
  private selectedOptionPrefix = '';
  private optionsOrigin: {field: WorkforceFilter; prefix: string; snapshotId: string} | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private visible = false;
  private snapshotSession = false;
  private promotionRetry = false;

  constructor(private api: WorkforceApi) {}

  // Selector context shares the state lifetime, including recreated dialog steps.
  get optionField(): WorkforceFilter { return this.selectedOptionField; }
  set optionField(field: WorkforceFilter) {
    if (field === this.selectedOptionField) { return; }
    this.selectedOptionField = field; this.selectedOptionPrefix = ''; this.invalidateOptions();
  }
  get optionPrefix(): string { return this.selectedOptionPrefix; }
  set optionPrefix(prefix: string) {
    if (prefix === this.selectedOptionPrefix) { return; }
    this.selectedOptionPrefix = prefix; this.invalidateOptions();
  }
  private invalidateOptions(): void {
    ++this.optionsVersion; this.options = []; this.optionsNext = null;
    this.optionsOrigin = null; this.optionsLoading = false;
  }
  canChooseOption(value: string): boolean {
    return !this.optionsLoading && this.mode === 'snapshot' && this.options.includes(value)
      && this.optionsOrigin?.field === this.optionField && this.optionsOrigin?.prefix === this.optionPrefix
      && this.optionsOrigin?.snapshotId === this.context?.snapshot?.id;
  }

  toggleSelection(row: WorkforceEmployee): void {
    if (this.selection.has(row.employeeKey)) { this.selection.delete(row.employeeKey); ++this.selectionRevision; return; }
    if (this.mode !== 'snapshot' || !this.context?.snapshot || this.selection.size >= 100
      || row.activeEligibility !== 'eligible' || row.employmentStatus?.trim().toLowerCase() !== 'active') { return; }
    ++this.selectionRevision;
    this.selectionSnapshotId = this.context.snapshot.id;
    this.selection.set(row.employeeKey, {employeeKey: row.employeeKey, name: `${row.firstName || ''} ${row.lastName || ''}`.trim()});
  }
  isSelected(row: WorkforceEmployee): boolean { return this.selection.has(row.employeeKey); }
  protected onSnapshotChanged(): void {}
  canSelect(row: WorkforceEmployee): boolean {
    return row.activeEligibility === 'eligible' && row.employmentStatus?.trim().toLowerCase() === 'active';
  }
  get unselectedPageRows(): WorkforceEmployee[] { return this.rows.filter(row => this.canSelect(row) && !this.selection.has(row.employeeKey)); }
  get canSelectPage(): boolean { return !this.loading && this.unselectedPageRows.length > 0 && this.selection.size + this.unselectedPageRows.length <= 100; }
  selectPage(): void {
    if (!this.canSelectPage) { return; }
    this.unselectedPageRows.forEach(row => this.toggleSelection(row));
  }
  get rows(): WorkforceEmployee[] { return this.page?.employees || []; }
  get warnings(): string[] {
    const messages: Record<string, string> = {
      optional_fields_missing: 'Some optional employee information is missing.',
      optional_resource_failure: 'Some optional information could not be retrieved during synchronization.',
      primary_email_missing: 'Some employees have no primary email. They remain part of this verified population.',
    };
    return (this.context?.snapshot?.warningCodes || []).map(code => messages[code]).filter(Boolean);
  }
  get age(): string {
    if (!this.context?.snapshot) return '';
    const minutes = Math.max(0, Math.floor((Date.parse(this.context.serverTime) - Date.parse(this.context.snapshot.builtAt)) / 60000));
    return minutes < 60 ? `${minutes} minutes old` : minutes < 1440 ? `${Math.floor(minutes / 60)} hours old` : `${Math.floor(minutes / 1440)} days old`;
  }

  clear(): void {
    ++this.selectionRevision;
    ++this.version;
    this.invalidateOptions();
    this.selectedOptionField = 'department'; this.selectedOptionPrefix = '';
    this.stopTimer();
    this.selection.clear(); this.selectionSnapshotId = null;
    this.context = null;
    this.page = null;
    this.options = [];
    this.optionsNext = null;
    this.history = [];
    this.currentCursor = null;
    this.loading = false;
    this.optionsLoading = false;
  }
  destroy(): void { this.visible = false; this.clear(); this.mode = 'blocked'; }
  // Failed reads and same-company retries invalidate access evidence, not necessarily owner input.
  protected invalidateAccess(): void { this.clear(); }
  private stopTimer(): void { if (this.timer) clearTimeout(this.timer); this.timer = undefined; }
  private fail(): void {
    this.invalidateAccess();
    this.mode = 'blocked';
    this.error = 'Workforce browsing is unavailable. Reopen this page to resolve access and connection state.';
  }
  private accept(context: WorkforceContext): boolean {
    if (context.scope.companyId !== this.companyId) { this.fail(); return false; }
    if (context.mode === 'legacy') {
      if (this.snapshotSession) { this.fail(); return false; }
      this.mode = 'legacy'; this.context = context; return true;
    }
    if (context.mode !== 'snapshot' || context.scope.providerSlug !== 'ukg_ready' || context.scope.providerSlot !== 'primary') {
      this.fail(); return false;
    }
    if (this.context?.snapshot?.id && context.snapshot?.id !== this.context.snapshot.id
      || this.selectionSnapshotId && context.snapshot?.id !== this.selectionSnapshotId) {
      if (this.selectionSnapshotId) { this.notice = 'The source snapshot changed. Review employees and select them again.'; }
      this.selection.clear(); this.selectionSnapshotId = null; this.onSnapshotChanged();
    }
    this.snapshotSession = true;
    this.mode = 'snapshot';
    this.context = context;
    return true;
  }

  async open(companyId: string): Promise<void> {
    const sameCompany = companyId === this.companyId;
    if (!sameCompany) {
      this.snapshotSession = false;
      this.query = {sort: 'name', order: 'asc', searchField: 'lastName', search: ''};
    }
    if (sameCompany) this.invalidateAccess(); else this.clear();
    this.companyId = companyId;
    this.mode = 'resolving';
    this.error = ''; this.notice = '';
    const version = this.version;
    try {
      const context = await this.api.getWorkforceBrowseContext(companyId);
      if (version !== this.version) return;
      if (this.accept(context) && this.visible && context.mode === 'snapshot') await this.loadPage();
    } catch (_) { if (version === this.version) this.fail(); }
  }

  async setVisible(visible: boolean): Promise<void> {
    const wasVisible = this.visible;
    this.visible = visible;
    this.stopTimer();
    if (!visible) {
      if (!wasVisible) return;
      // Navigation invalidates in-flight responses and releases employee rows immediately.
      ++this.version; ++this.optionsVersion;
      this.page = null; this.options = []; this.optionsNext = null;
      this.history = []; this.currentCursor = null;
      this.loading = false; this.optionsLoading = false;
      return;
    }
    if (this.mode === 'snapshot') await this.reload();
    else if (this.mode === 'resolving' && this.companyId) await this.open(this.companyId);
  }

  async setQuery(query: Record<string, string>): Promise<void> {
    this.query = {...query};
    this.history = []; this.currentCursor = null;
    this.options = []; this.optionsNext = null; ++this.optionsVersion;
    this.optionsLoading = false;
    await this.loadPage();
  }
  async next(): Promise<void> {
    if (this.loading || !this.page?.nextCursor) return;
    this.history.push(this.currentCursor);
    if (this.history.length > this.historyLimit) this.history.shift();
    this.currentCursor = this.page.nextCursor;
    await this.loadPage();
  }
  async previous(): Promise<void> {
    if (this.loading || !this.history.length) return;
    this.currentCursor = this.history.pop() || null;
    await this.loadPage();
  }
  private schedule(): void {
    this.stopTimer();
    if (this.visible && this.mode === 'snapshot' && this.context?.synchronization?.active) {
      this.timer = setTimeout(() => { void this.poll(); }, 10000);
    }
  }
  private async poll(): Promise<void> {
    const version = this.version;
    try {
      const context = await this.api.getWorkforceBrowseContext(this.companyId);
      if (version !== this.version || !this.visible) return;
      const changed = context.snapshot?.id !== this.context?.snapshot?.id;
      if (!this.accept(context)) return;
      if (changed) { await this.resetPromotion(); return; }
      if (context.availability !== 'ready') this.page = null;
      this.schedule();
    } catch (_) { if (version === this.version) this.fail(); }
  }
  async reload(): Promise<void> {
    this.stopTimer();
    const version = ++this.version;
    ++this.optionsVersion;
    this.optionsLoading = false;
    this.page = null; this.options = []; this.optionsNext = null;
    this.history = []; this.currentCursor = null; this.loading = true;
    try {
      const context = await this.api.getWorkforceBrowseContext(this.companyId);
      if (version !== this.version) return;
      if (this.accept(context) && this.mode === 'snapshot') await this.loadPage();
    } catch (_) { if (version === this.version) this.fail(); }
    finally { if (version === this.version) this.loading = false; }
  }
  private async resetPromotion(): Promise<void> {
    if (this.promotionRetry) { this.fail(); return; }
    this.notice = 'A newer workforce snapshot is available. Showing its first page.';
    this.promotionRetry = true;
    try { await this.reload(); } finally { this.promotionRetry = false; }
  }
  private async loadPage(): Promise<void> {
    if (this.mode !== 'snapshot') return;
    this.stopTimer();
    const version = ++this.version;
    this.page = null; this.loading = true; this.error = '';
    if (this.context?.availability !== 'ready' || !this.context.snapshot) {
      this.loading = false; this.schedule(); return;
    }
    const snapshotId = this.context.snapshot.id;
    try {
      const page = await this.api.getWorkforceEmployees(this.companyId, {...this.query, limit: '50', snapshotId,
        ...(this.currentCursor ? {cursor: this.currentCursor} : {})});
      if (version !== this.version) return;
      if (page.snapshot?.id !== snapshotId && page.availability === 'ready') { await this.resetPromotion(); return; }
      if (!this.accept(page) || this.mode !== 'snapshot') return;
      if (page.employees.length > 50) { this.fail(); return; }
      // Keep metadata separate from rows so clearing a page cannot retain its employees in context.
      const {employees, nextCursor, previousCursor, ...context} = page;
      this.context = context;
      this.page = { ...context, employees, nextCursor, previousCursor };
      this.schedule();
    } catch (caught: any) {
      if (version !== this.version) return;
      const code = caught?.data?.code || caught?.error?.code || caught?.code;
      if (code === 'WORKFORCE_SNAPSHOT_CHANGED') { await this.resetPromotion(); return; }
      this.fail();
    } finally { if (version === this.version) this.loading = false; }
  }
  async loadOptions(field: WorkforceFilter, prefix: string, more = false): Promise<void> {
    if (this.mode !== 'snapshot' || !this.context?.snapshot) return;
    this.optionField = field; this.optionPrefix = prefix;
    const version = this.version;
    const optionsVersion = ++this.optionsVersion;
    const cursor = more ? this.optionsNext : null;
    this.options = []; this.optionsNext = null; this.optionsOrigin = null; this.optionsLoading = true;
    try {
      const response = await this.api.getWorkforceFilterOptions(this.companyId, {field, prefix,
        snapshotId: this.context.snapshot.id, ...(cursor ? {cursor} : {})});
      if (version !== this.version || optionsVersion !== this.optionsVersion) return;
      if (response.values.length > 50 || response.snapshot?.id !== this.context.snapshot.id) { this.fail(); return; }
      this.optionsOrigin = {field, prefix, snapshotId: this.context.snapshot.id};
      this.options = response.values; this.optionsNext = response.nextCursor;
    } catch (caught: any) {
      if (version !== this.version || optionsVersion !== this.optionsVersion) return;
      if ((caught?.data?.code || caught?.error?.code || caught?.code) === 'WORKFORCE_SNAPSHOT_CHANGED') await this.resetPromotion();
      else this.fail();
    } finally { if (optionsVersion === this.optionsVersion) this.optionsLoading = false; }
  }
}
